'use client';

import {useActionState} from 'react';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';

import {addToCart, updateCart} from '@/modules/commerce/cart/actions';
import {EmbroideryOptionSelector} from '@/modules/commerce/embroidery-collection/EmbroideryOptionSelector';
import {EmbroideryTextOptionSelector} from '@/modules/commerce/embroidery-text/components/EmbroideryTextOptionSelector';
import {CheckboxTile} from '@/modules/commerce/form/CheckboxTile';
import {FormattedItemPrice} from '@/modules/commerce/item/FormattedItemPrice';
import type {ItemWithVatTypeFragment} from '@/modules/commerce/item/query';
import {
	cabTableConfigurationFromFormData,
	getDefaultCabTableConfiguration,
	getFilteredEmbroideryOptionForCabTable,
	getItemsForCabTableConfiguration,
	validateCabTableConfiguration,
} from '@/modules/commerce/product/cab-table/configuration';
import {CupHolderSelector} from '@/modules/commerce/product/cab-table/CupHolderSelector';
import {PowerOutletSelector} from '@/modules/commerce/product/cab-table/PowerOutletSelector';
import type {CabTableProductFragment} from '@/modules/commerce/product/cab-table/query';
import {
	productConfigurationFromSearchParams,
	searchParamsFromProductConfiguration,
} from '@/modules/commerce/product/configuration';
import {ProductImageCarousel} from '@/modules/commerce/product/ProductImageCarousel';
import {ProductMaterialOptionSelector} from '@/modules/commerce/product-material/ProductMaterialOptionSelector';
import {Alert, AlertBody, AlertHeading} from '@/modules/foundation/components/alert';
import {Breadcrumb} from '@/modules/foundation/components/breadcrumb';
import {Button} from '@/modules/foundation/components/button';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {TextHeading} from '@/modules/page/components/page-head/text-heading';

import {PRODUCT_PAGE_ALL_SLUG} from '../schema/constants';

export type CabTableProductPageProps = {
	product: CabTableProductFragment;
	shoppingDisabled?: boolean;
};

export default function CabTableProductPage({product, shoppingDisabled}: CabTableProductPageProps) {
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();
	const [addToCartState, addToCartAction, isAddingToCart] = useActionState(addToCart, {});
	const [updateCartState, updateCartAction, isUpdatingCart] = useActionState(updateCart, {});

	const cartLineId = searchParams.get('cartLineId') ?? null;

	const configuration =
		productConfigurationFromSearchParams(product, searchParams) ??
		getDefaultCabTableConfiguration(product);
	const validation = validateCabTableConfiguration(product, configuration);
	const isValid = validation?.isValid;

	let selectedItems: ItemWithVatTypeFragment[] = [];
	if (isValid) {
		selectedItems = getItemsForCabTableConfiguration(product, configuration);
	}

	const category = product.category;
	const subcategory = product.subcategory;

	const productSlug = `${subcategory?.fullSlug}/${product.slug?.current}`;

	// Get the brand ids of the product and its compatible vehicles. We only want to show
	// embroidery options for these brands.
	const embroideryOption = getFilteredEmbroideryOptionForCabTable(product);

	// FIXME: need to check out how to use product image with logo if it's available
	// FIXME: products needs to have description
	// FIXME: Move this to the generateMetadata function@
	/*
	const productData: WithContext<Product> = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: product.name,
		brand: product.resolvedBrand?.name ?? undefined,
		category: product.subcategory?.name ?? undefined,
		offers: {
			'@type': 'Offer',
			price: priceIncVat.toString(),
			priceCurrency: 'NOK',
			itemCondition: 'https://schema.org/NewCondition',
			url: `${siteUrl}/${productSlug}`,
		},
	};
	*/

	return (
		<>
			<TextHeading
				heading={product.name}
				breadcrumb={
					<Breadcrumb
						breadcrumb={[
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}`,
								title: 'Produkter',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${category?.slug}`,
								title: category?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${subcategory?.fullSlug}`,
								title: subcategory?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${productSlug}`,
								title: product.name ?? '',
								active: true,
							},
						]}
					/>
				}
			>
				<Paragraph>{product.supplier?.name}</Paragraph>
			</TextHeading>
			<Container width="normal" spacingBottom="normal" spacingTop="small">
				<div className="md:flex md:flex-row md:items-start md:gap-8">
					{/*<JsonLd jsonLd={productData} />*/}
					<div className="mb-8 md:sticky md:top-8 md:w-1/2">
						<ProductImageCarousel product={product} config={configuration} />
					</div>

					<form
						className="md:w-1/2"
						action={cartLineId ? updateCartAction : addToCartAction}
						onChange={(e) => {
							// TODO: Debounce these events to avoid many consecutive reloads

							// Update the page search params with the new configuration:
							const formData = new FormData(e.currentTarget);
							const configuration = cabTableConfigurationFromFormData(
								product,
								formData,
							);

							if (configuration) {
								const params = searchParamsFromProductConfiguration(configuration);
								if (cartLineId) {
									params.set('cartLineId', cartLineId);
								}

								router.replace(`${pathname}?${params.toString()}`, {
									scroll: false,
								});
							}
						}}
					>
						<input type="hidden" name="productId" value={product._id} />
						{cartLineId?.length ? (
							<input
								name="cartLineId"
								defaultValue={cartLineId}
								className="sr-only"
							/>
						) : null}

						{product.description && (
							<div className="mb-8">
								<PortableText value={product.description} />
							</div>
						)}

						<div className="mb-8">
							<Heading level={2} size="h3" className="mb-3">
								Bordmateriale
							</Heading>
							<ProductMaterialOptionSelector
								namePrefix="baseMaterial"
								options={product.settings?.baseMaterials ?? []}
								defaultSelection={configuration.baseMaterial}
							/>
						</div>

						<div className="mb-8">
							<Heading level={2} size="h3" className="mb-3">
								Kantmateriale
							</Heading>
							<ProductMaterialOptionSelector
								namePrefix="edgeMaterial"
								options={product.settings?.edgeMaterials ?? []}
								defaultSelection={configuration.edgeMaterial}
							/>
						</div>

						{product.settings?.cupHolder?.available && (
							<CupHolderSelector
								legend="Koppholdere"
								namePrefix="cupHolders"
								maxQuantity={product.settings.cupHolder?.maxQuantity || 0}
								items={product.settings.cupHolder.items || []}
								defaultQuantity={configuration.cupHolders ?? null}
								className="mb-8"
							/>
						)}

						{product.settings?.powerOutlet?.available && (
							<div className="mb-8">
								<Heading level={2} size="h3" className="mb-3">
									Strømuttak
								</Heading>
								<PowerOutletSelector
									types={product.settings.powerOutlet.types || []}
									maxQuantity={product.settings.powerOutlet?.maxQuantity || 0}
									namePrefix="powerOutlets"
									defaultConfig={configuration.powerOutlets ?? null}
								/>
							</div>
						)}

						{(embroideryOption?.available ||
							product.settings?.ledLighting?.available ||
							product.settings?.mountingBracket?.available ||
							product.settings?.paperClip?.available) && (
							<div className="mb-8">
								<Heading level={2} size="h3" className="mb-3">
									Tilpasninger
								</Heading>
								<div className="flex flex-col gap-4">
									{embroideryOption?.available && (
										<EmbroideryOptionSelector
											legend="Broderi"
											namePrefix="embroidery"
											option={embroideryOption}
											defaultConfiguration={configuration.embroidery}
										/>
									)}
									{product.settings?.embroideryText?.available && (
										<EmbroideryTextOptionSelector
											legend="Brodert tekst"
											namePrefix="embroideryText"
											option={product.settings.embroideryText}
											defaultConfiguration={configuration.embroideryText}
										/>
									)}
									{product.settings?.ledLighting?.available && (
										<CheckboxTile
											name="ledLighting"
											value="true"
											defaultChecked={configuration.ledLighting ?? false}
										>
											LED-belysning{' '}
											{product.settings.ledLighting?.items &&
												product.settings.ledLighting?.items.length > 0 && (
													<>
														{'(+ '}
														<FormattedItemPrice
															items={
																product.settings.ledLighting?.items
															}
														/>
														{')'}
													</>
												)}
										</CheckboxTile>
									)}

									{product.settings?.mountingBracket?.available && (
										<CheckboxTile
											name="mountingBracket"
											value="true"
											defaultChecked={configuration.mountingBracket ?? false}
										>
											Monteringsbrakett{' '}
											{product.settings.mountingBracket?.items &&
												product.settings.mountingBracket?.items.length >
													0 && (
													<>
														{'(+ '}
														<FormattedItemPrice
															items={
																product.settings.mountingBracket
																	?.items
															}
														/>
														{')'}
													</>
												)}
										</CheckboxTile>
									)}

									{product.settings?.paperClip?.available && (
										<CheckboxTile
											name="paperClip"
											value="true"
											defaultChecked={configuration.paperClip ?? false}
										>
											Papirklips{' '}
											{product.settings.paperClip?.items &&
												product.settings.paperClip?.items.length > 0 && (
													<>
														{'(+ '}
														<FormattedItemPrice
															items={
																product.settings.paperClip?.items
															}
														/>
														{')'}
													</>
												)}
										</CheckboxTile>
									)}
								</div>
							</div>
						)}

						{selectedItems && !!selectedItems.length && (
							<div className="text-primary font-display mb-8 text-4xl">
								<h2 className="sr-only">Pris</h2>
								<FormattedItemPrice items={selectedItems} />
							</div>
						)}

						{!validation?.isValid && (
							<Alert severity="warning" className="mb-8">
								<AlertHeading>Ugyldig konfigurasjon</AlertHeading>
								<AlertBody>
									Valgt konfigurasjon er ikke gyldig og kan ikke legges i
									handlekurven. Vennligst se over feilmeldingene over og gjør
									nødvendige endringer.
								</AlertBody>
							</Alert>
						)}
						{(addToCartState.error || updateCartState.error) && (
							<Alert severity="danger" className="mb-8">
								<AlertHeading>Feil med handlekurv</AlertHeading>
								<AlertBody className="mb-2">
									Noe gikk galt ved oppdatering av handlekurven. Vennligst prøv
									igjen, men kontakt oss hvis problemet vedvarer.
								</AlertBody>
								<AlertBody>
									Systemet sa{' '}
									<strong>
										&laquo;
										{addToCartState.error}
										{updateCartState.error}&raquo;
									</strong>
									.
									<br />
								</AlertBody>
							</Alert>
						)}
						<Button
							type="submit"
							disabled={
								shoppingDisabled || !isValid || isAddingToCart || isUpdatingCart
							}
							size="lg"
						>
							{isAddingToCart || isUpdatingCart
								? cartLineId
									? 'Oppdaterer handlevognen'
									: 'Legger til i handlekurv...'
								: cartLineId
									? 'Oppdater handlevognen'
									: 'Legg i handlekurv'}
						</Button>
						{shoppingDisabled && (
							<Paragraph className="mt-4">
								Netthandel er midlertidig deaktivert. Sjekk tilbake senere.
							</Paragraph>
						)}
					</form>
				</div>
			</Container>
		</>
	);
}
